/**
 * launcher 位置的持久化。
 *
 * localStorage 優先 —— 它是同步的，首次 render 就拿得到位置。第三方 iframe 的
 * storage 會被部分瀏覽器的隱私設定分區或直接封鎖（既有 getExpandedPreference 已經
 * 為此包了 try/catch），這時退到宿主頁 cookie：走現成的 notifySetCookie /
 * notifyGetCookie 通道，代價是讀取變成跨 frame 往返，首次 render 會晚一步拿到值。
 *
 * 序列化格式刻意用 `edge:offsetY` 而不是 JSON —— 同一份字串在 localStorage 與
 * cookie 兩條路上都能直接存，不需要處理 cookie 的字元轉義。
 */

import { Anchor, parseAnchor, positionStorageKey, serializeAnchor } from './geometry';
import { getParentCookie, setParentCookie } from './Util';

const COOKIE_EXPIRE_DAYS = 365;

export const readLocalAnchor = (orgId: string): Anchor | null => {
	try {
		return parseAnchor(window.localStorage.getItem(positionStorageKey(orgId)));
	} catch (e) {
		return null;
	}
};

/**
 * 寫入 localStorage，回傳是否真的寫進去了。
 *
 * 不能只靠 try/catch —— 部分瀏覽器的分區 storage 會靜默丟棄寫入而不拋例外，所以
 * 讀回來比對，失敗才切換到 cookie 退路。
 */
export const writeLocalAnchor = (orgId: string, anchor: Anchor): boolean => {
	const key = positionStorageKey(orgId);
	const value = serializeAnchor(anchor);

	try {
		window.localStorage.setItem(key, value);
		return window.localStorage.getItem(key) === value;
	} catch (e) {
		return false;
	}
};

/**
 * 請求宿主頁的位置 cookie。回應走 getParentCookie 指令，依 name 分流：位置由
 * useWidgetGeometry 接收，App 只收 customer token（見 parentCookieToken.ts）
 */
export const requestParentAnchor = (orgId: string) => {
	getParentCookie(positionStorageKey(orgId));
};

export const writeParentAnchor = (orgId: string, anchor: Anchor) => {
	setParentCookie(positionStorageKey(orgId), serializeAnchor(anchor), COOKIE_EXPIRE_DAYS);
};

/** 寫入位置：localStorage 優先，失敗才退到宿主頁 cookie。回傳實際採用的路徑 */
export const persistAnchor = (orgId: string, anchor: Anchor): 'local' | 'parent-cookie' => {
	if (writeLocalAnchor(orgId, anchor)) {
		return 'local';
	}

	writeParentAnchor(orgId, anchor);
	return 'parent-cookie';
};
